import React from 'react';
import './src/utils/consoleCapture';
import { NavigationContainer } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { Platform } from 'react-native';
import { ThemeProvider } from './src/context/ThemeContext';
import { UserPreferencesProvider } from './src/context/UserPreferencesContext';
import SplashScreen from './src/screens/SplashScreen';
import LoginScreen from './src/screens/LoginScreen';
import ForgotPasswordScreen from './src/screens/ForgotPasswordScreen';
import ResetPasswordScreen from './src/screens/ResetPasswordScreen';
import MainPage from './src/screens/MainPage';
import SessionBuilder from './src/screens/SessionBuilder';
import MyCalendarPage from './src/screens/MyCalendarPage';
import ProfilePage from './src/screens/ProfilePage';
import BuilderMainPage from './src/screens/BuilderMainPage';
import ChangeGradePage from './src/screens/ChangeGradePage';
import CreateUserPage from './src/screens/CreateUserPage';
import ExerciseBuilder from './src/screens/ExerciseBuilder';
import ExerciseTypeBuilder from './src/screens/ExerciseTypeBuilder';
import CourseBuilder from './src/screens/CourseBuilder';
import QuotesBuilder from './src/screens/QuotesBuilder';
import RecordsPage from './src/screens/RecordsPage';
import AssignmentsPage from './src/screens/AssignmentsPage';
import SchemaBuilderPage from './src/screens/SchemaBuilderPage';
import BreaksBuilder from './src/screens/BreaksBuilder';
import MyBreaksPage from './src/screens/MyBreaksPage';
import CreateBreaksBuilder from './src/screens/CreateBreaksBuilder';
import GoalsPage from './src/screens/GoalsPage';
import UserAuditPage from './src/screens/UserAuditPage';
import MyCoursesPage from './src/screens/MyCoursesPage';
import MyCourseDetailPage from './src/screens/MyCourseDetailPage';
import WhatShouldITrainPage from './src/screens/WhatShouldITrainPage';
import ClubStatsPage from './src/screens/ClubStatsPage';
import SyllabusPage from './src/screens/SyllabusPage';
import UploadNewBreaksPage from './src/screens/UploadNewBreaksPage';
import SendNotificationsPage from './src/screens/SendNotificationsPage';
import StoryBuilderPage from './src/screens/StoryBuilderPage';
import CourseEditorPage from './src/screens/CourseEditorPage';
import AdoptionPage from './src/screens/AdoptionPage';
import AdoptionReportsPage from './src/screens/AdoptionReportsPage';
import AssignmentsBuilderPage from './src/screens/AssignmentsBuilderPage';
import RoutineBuilder from './src/screens/RoutineBuilder';
import RecordPageLayoutBuilderPage from './src/screens/RecordPageLayoutBuilderPage';
import RecordDetailPage from './src/screens/RecordDetailPage';
import ChiGungHandbookPage from './src/screens/ChiGungHandbookPage';
import ChiGungHandbookFormPage from './src/screens/ChiGungHandbookFormPage';
import DimMakBuilderHubPage from './src/screens/DimMakBuilderHubPage';
import DimMakMeridiansBuilderPage from './src/screens/DimMakMeridiansBuilderPage';
import DimMakPointsBuilderPage from './src/screens/DimMakPointsBuilderPage';
import DimMakHandbookPage from './src/screens/DimMakHandbookPage';
import DimMakMeridianPage from './src/screens/DimMakMeridianPage';
import { DIM_MAK_MERIDIANS } from './src/utils/dimMakMeridians';
import StudentNotesPage from './src/screens/StudentNotesPage';
import GettingStartedPage from './src/screens/GettingStartedPage';
import ClassBookPage from './src/screens/ClassBookPage';
import ClassBuilderPage from './src/screens/ClassBuilderPage';
import YoutubeOAuthBridgePage from './src/screens/YoutubeOAuthBridgePage';
import Tao2Page from './src/screens/Tao2Page';
import { withBuilderAccess } from './src/navigation/withBuilderAccess';
import {
  canAccessBuilderMain,
  canAccessBuilderScreen,
  getBuilderAccessDeniedMessage,
  canAccessTao2,
  TAO2_ACCESS_DENIED_MESSAGE,
  BUILDER_MAIN_DENIED_MESSAGE,
} from './src/utils/builderAccess';

const ProtectedBuilderMain = withBuilderAccess(BuilderMainPage, 'Builders', {
  canAccess: canAccessBuilderMain,
  deniedMessage: BUILDER_MAIN_DENIED_MESSAGE,
});
const ProtectedChangeGrade = withBuilderAccess(ChangeGradePage, 'Change Grade');
const ProtectedCreateUser = withBuilderAccess(CreateUserPage, 'Create/Edit Users');
const ProtectedExerciseBuilder = withBuilderAccess(ExerciseBuilder, 'Exercise Builder');
const ProtectedExerciseTypeBuilder = withBuilderAccess(ExerciseTypeBuilder, 'Exercise Type Builder');
const ProtectedCourseBuilder = withBuilderAccess(CourseBuilder, 'Course Builder');
const ProtectedQuotesBuilder = withBuilderAccess(QuotesBuilder, 'Quotes Builder');
const ProtectedBreaksBuilder = withBuilderAccess(BreaksBuilder, 'Upload Existing Breaks');
const ProtectedCreateBreaksBuilder = withBuilderAccess(CreateBreaksBuilder, 'Create Breaks Builder');
const ProtectedStoryBuilder = withBuilderAccess(StoryBuilderPage, 'Story Builder', {
  canAccess: (user) => canAccessBuilderScreen('StoryBuilder', user),
  deniedMessage: getBuilderAccessDeniedMessage('StoryBuilder'),
});
const ProtectedCourseEditor = withBuilderAccess(CourseEditorPage, 'Course Editor');
const ProtectedAssignmentsBuilder = withBuilderAccess(AssignmentsBuilderPage, 'Assignments Builder');
const ProtectedRoutineBuilder = withBuilderAccess(RoutineBuilder, 'Routine Builder', {
  canAccess: (user) => canAccessBuilderScreen('RoutineBuilder', user),
  deniedMessage: getBuilderAccessDeniedMessage('RoutineBuilder'),
});
const ProtectedRecordPageLayoutBuilder = withBuilderAccess(
  RecordPageLayoutBuilderPage,
  'Record Page Layout'
);
const ProtectedClassBuilder = withBuilderAccess(ClassBuilderPage, 'Class Builder');
const ProtectedDimMakBuilder = withBuilderAccess(DimMakBuilderHubPage, 'Dim mak builder');
const ProtectedDimMakMeridiansBuilder = withBuilderAccess(
  DimMakMeridiansBuilderPage,
  'Dim mak Meridians'
);
const ProtectedDimMakPointsBuilder = withBuilderAccess(
  DimMakPointsBuilderPage,
  'Dim Mak Meridian points'
);
const ProtectedTao2 = withBuilderAccess(Tao2Page, 'Tao 2', {
  canAccess: canAccessTao2,
  deniedMessage: TAO2_ACCESS_DENIED_MESSAGE,
});

// Import global CSS for web
if (Platform.OS === 'web') {
  require('./src/styles/global.css');
  const {
    installYoutubeOAuthReturnListener,
    restoreYoutubeOAuthReturnHrefIfPresent,
  } = require('./src/utils/youtubeOAuthFlow');
  if (!restoreYoutubeOAuthReturnHrefIfPresent()) {
    installYoutubeOAuthReturnListener();
  }
}

const Stack = createNativeStackNavigator();

const webLinking =
  Platform.OS === 'web' && typeof window !== 'undefined'
    ? {
        prefixes: [`${window.location.origin}/`],
        config: {
          screens: {
            Splash: '',
            Login: 'login',
            ForgotPassword: 'forgot-password',
            ResetPassword: 'reset-password',
            MainPage: 'home',
            MyCourses: 'mycourses',
            MyCourseDetail: 'mycourse/:courseId',
            WhatShouldITrain: 'whatshoulditrain',
            ClubStats: 'club-stats',
            Syllabus: 'syllabus',
            UploadNewBreaks: 'upload-new-breaks',
            SendNotifications: 'send-message',
            StudentNotes: 'student-notes',
            GettingStarted: 'getting-started',
            ClassBook: 'classbook',
            Tao2: 'tao-2',
            MyCalendar: 'my-calendar',
            YoutubeOAuthBridge: 'youtube-oauth',
          },
        },
      }
    : undefined;

export default function App() {
  return (
    <ThemeProvider>
      <UserPreferencesProvider>
      <NavigationContainer linking={webLinking}>
        <Stack.Navigator
          initialRouteName="Splash"
          screenOptions={{
            headerShown: false,
          }}
        >
          <Stack.Screen name="Splash" component={SplashScreen} />
          <Stack.Screen name="Login" component={LoginScreen} />
          <Stack.Screen name="ForgotPassword" component={ForgotPasswordScreen} />
          <Stack.Screen name="ResetPassword" component={ResetPasswordScreen} />
          <Stack.Screen name="MainPage" component={MainPage} />
          <Stack.Screen name="MyCourses" component={MyCoursesPage} />
          <Stack.Screen name="MyCourseDetail" component={MyCourseDetailPage} />
          <Stack.Screen name="ChiGungHandbook" component={ChiGungHandbookPage} />
          <Stack.Screen name="ChiGungHandbookForm" component={ChiGungHandbookFormPage} />
          <Stack.Screen name="ChiGungHandbookFormComment" component={ChiGungHandbookFormPage} />
          <Stack.Screen name="DimMakHandbook" component={DimMakHandbookPage} />
          {DIM_MAK_MERIDIANS.map((meridian) => (
            <Stack.Screen
              key={meridian.screen}
              name={meridian.screen}
              component={DimMakMeridianPage}
            />
          ))}
          <Stack.Screen name="DimMakBuilder" component={ProtectedDimMakBuilder} />
          <Stack.Screen name="DimMakMeridiansBuilder" component={ProtectedDimMakMeridiansBuilder} />
          <Stack.Screen name="DimMakPointsBuilder" component={ProtectedDimMakPointsBuilder} />
          <Stack.Screen name="SessionBuilder" component={SessionBuilder} />
          <Stack.Screen name="MyCalendar" component={MyCalendarPage} />
          <Stack.Screen name="Profile" component={ProfilePage} />
          <Stack.Screen name="BuilderMain" component={ProtectedBuilderMain} />
          <Stack.Screen name="ChangeGrade" component={ProtectedChangeGrade} />
          <Stack.Screen name="CreateUser" component={ProtectedCreateUser} />
          <Stack.Screen name="ExerciseBuilder" component={ProtectedExerciseBuilder} />
          <Stack.Screen name="ExerciseTypeBuilder" component={ProtectedExerciseTypeBuilder} />
          <Stack.Screen name="CourseBuilder" component={ProtectedCourseBuilder} />
          <Stack.Screen name="QuotesBuilder" component={ProtectedQuotesBuilder} />
          <Stack.Screen name="Records" component={RecordsPage} />
          <Stack.Screen name="Assignments" component={AssignmentsPage} />
          <Stack.Screen name="SchemaBuilder" component={SchemaBuilderPage} />
          <Stack.Screen name="BreaksBuilder" component={ProtectedBreaksBuilder} />
          <Stack.Screen name="MyBreaks" component={MyBreaksPage} />
          <Stack.Screen name="CreateBreaksBuilder" component={ProtectedCreateBreaksBuilder} />
          <Stack.Screen name="Goals" component={GoalsPage} />
          <Stack.Screen name="UserAudit" component={UserAuditPage} />
          <Stack.Screen name="Adoption" component={AdoptionPage} />
          <Stack.Screen name="AdoptionReports" component={AdoptionReportsPage} />
          <Stack.Screen name="WhatShouldITrain" component={WhatShouldITrainPage} />
          <Stack.Screen name="ClubStats" component={ClubStatsPage} />
          <Stack.Screen name="Syllabus" component={SyllabusPage} />
          <Stack.Screen name="UploadNewBreaks" component={UploadNewBreaksPage} />
          <Stack.Screen name="SendNotifications" component={SendNotificationsPage} />
          <Stack.Screen name="StoryBuilder" component={ProtectedStoryBuilder} />
          <Stack.Screen name="CourseEditor" component={ProtectedCourseEditor} />
          <Stack.Screen name="AssignmentsBuilder" component={ProtectedAssignmentsBuilder} />
          <Stack.Screen name="RoutineBuilder" component={ProtectedRoutineBuilder} />
          <Stack.Screen name="RecordPageLayoutBuilder" component={ProtectedRecordPageLayoutBuilder} />
          <Stack.Screen name="RecordDetail" component={RecordDetailPage} />
          <Stack.Screen name="StudentNotes" component={StudentNotesPage} />
          <Stack.Screen name="GettingStarted" component={GettingStartedPage} />
          <Stack.Screen name="ClassBook" component={ClassBookPage} />
          <Stack.Screen name="Tao2" component={ProtectedTao2} />
          <Stack.Screen name="ClassBuilder" component={ProtectedClassBuilder} />
          <Stack.Screen name="YoutubeOAuthBridge" component={YoutubeOAuthBridgePage} />
        </Stack.Navigator>
      </NavigationContainer>
      </UserPreferencesProvider>
    </ThemeProvider>
  );
}

